import type { PointerEvent as ReactPointerEvent } from "react";

import { randomId } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";

/**
 * トーストの閉じ操作を「本体クリック」と「右スワイプ（ドラッグ）」に対応させる。
 * X ボタンは mantine-theme.ts の Notification.defaultProps で非表示にしている。
 *
 * 全呼び出し箇所（notifications.show(...)）を書き換える代わりに、show を一度だけラップして
 * onPointerDown / onClick を注入する。Mantine 標準にスワイプ消去は無いため、pointer イベントで
 * 自前実装する（指/ポインタに追従 → 一定量右に振り切ったら hide、届かなければ元位置へ戻す）。
 *
 * - 呼び出し側が id / onClick / onPointerDown を指定していれば尊重する。
 * - hide 対象を確定させるため、id 未指定のときはここで randomId を採番する。
 * - ドラッグ後の pointerup で発火する click は「消去」と二重にならないよう抑止する。
 */
const PATCHED = Symbol.for("kansai.notifications.gestures");

/** これ以上右に動かして離したら消す閾値（px と 幅比率のどちらか）。 */
const DISMISS_PX = 60;
const DISMISS_RATIO = 0.35;
/** クリックと区別するための「ドラッグとみなす」最小移動量。 */
const DRAG_SLOP = 4;
const ANIM_MS = 150;

interface GestureState {
  /** 直近の操作がドラッグだったか（true の間の click は消去扱いにしない）。 */
  dragged: boolean;
}

function startSwipe(
  event: ReactPointerEvent<HTMLElement>,
  id: string,
  state: GestureState,
): void {
  // 主ボタン（マウス左 / タッチ / ペン）のみ。右クリック等は無視。
  if (event.button !== 0) return;

  const el = event.currentTarget;
  const startX = event.clientX;
  const width = el.offsetWidth || 1;
  let dx = 0;

  state.dragged = false;
  el.style.transition = "none";
  try {
    el.setPointerCapture(event.pointerId);
  } catch {
    // 一部環境で capture 不可でも、以降のイベント購読で動作する。
  }

  const onMove = (e: PointerEvent): void => {
    dx = e.clientX - startX;
    if (Math.abs(dx) > DRAG_SLOP) state.dragged = true;
    const x = Math.max(0, dx); // 右方向のみ追従（左には動かさない）
    el.style.transform = `translateX(${x}px)`;
    el.style.opacity = String(Math.max(0, 1 - x / width));
  };

  const cleanup = (): void => {
    el.removeEventListener("pointermove", onMove);
    el.removeEventListener("pointerup", onUp);
    el.removeEventListener("pointercancel", onUp);
  };

  const onUp = (): void => {
    cleanup();
    el.style.transition = `transform ${ANIM_MS}ms ease, opacity ${ANIM_MS}ms ease`;
    const shouldDismiss = dx > DISMISS_PX || dx > width * DISMISS_RATIO;
    if (shouldDismiss) {
      el.style.transform = `translateX(${width}px)`;
      el.style.opacity = "0";
      window.setTimeout(() => notifications.hide(id), ANIM_MS);
    } else {
      // 閾値未満は元位置へスナップバック（消さない）。
      el.style.transform = "translateX(0)";
      el.style.opacity = "1";
    }
  };

  el.addEventListener("pointermove", onMove);
  el.addEventListener("pointerup", onUp);
  el.addEventListener("pointercancel", onUp);
}

export function installToastGestures(): void {
  const target = notifications as unknown as Record<PropertyKey, unknown>;
  // StrictMode / HMR による二重適用（=元 show の多重ラップ）を防ぐ。
  if (target[PATCHED]) return;
  target[PATCHED] = true;

  const originalShow = notifications.show.bind(notifications);
  // notifications.show は型上 read-only なので、キャストしたレコード経由で差し替える
  // （実体は書き換え可能なプレーンオブジェクト）。
  target.show = ((notification: Parameters<typeof notifications.show>[0]) => {
    const id = notification.id ?? randomId();
    const state: GestureState = { dragged: false };
    return originalShow({
      ...notification,
      id,
      onPointerDown: (event) => {
        notification.onPointerDown?.(event);
        startSwipe(event, id, state);
      },
      onClick: (event) => {
        notification.onClick?.(event);
        // ドラッグして離した直後の click は消去扱いにしない
        // （閾値未満で戻したのに click で消えてしまう誤動作を防ぐ）。
        if (state.dragged) {
          state.dragged = false;
          return;
        }
        notifications.hide(id);
      },
    });
  });
}
